import { z } from 'zod';
import { GeoPoint, Id, IdempotencyKey, Timestamp } from './primitives.js';
/**
 * FR-04: the per-visit photo cap. Enforced client-side and server-side.
 *
 * THIS IS THE ONLY PLACE THE NUMBER IS WRITTEN in TypeScript — everything else
 * imports it, including the copy the staff app renders. The one place it cannot
 * reach is SQL: `visit_photos_index_range` and
 * `visit_submissions_photo_count_range` are CHECK constraints, and a CHECK
 * cannot import a constant. `photo.test.ts` reads the migration and asserts the
 * two agree, because the failure mode when they drift is silent — presign and
 * the service check both pass and the INSERT dies on a constraint violation.
 *
 * Changing it means editing this line AND adding a migration. See
 * `packages/db/migrations/0046_photo_cap.sql`, and read its header before
 * lowering the number again — narrowing the CHECK is not the same operation as
 * widening it.
 */
export const MAX_PHOTOS_PER_VISIT = 7;
/**
 * FR-04 / FR-05: shelf photos are scored; menu pages feed the Price Index only.
 *
 * THE CLIENT NO LONGER CHOOSES THIS. It is the RESOLVED type — what the vision
 * model decided the photograph actually shows, or what a user later overrode it
 * to. A photo carries no type at all between upload and analysis, which is why
 * `PhotoRecord.photoType` and the `visit_photos.photo_type` column are both
 * nullable and `PhotoMetadataRequest` does not mention it.
 *
 * `PhotoAnalysis.photoType` is NOT nullable: an analysis is the thing that
 * resolves the question, so one without an answer is a contradiction.
 */
export const PhotoType = z.enum(['SHELF', 'MENU_BOOK']);
/** FR-04 permits both. Recorded because it is an FR-16 evaluation input. */
export const CaptureSource = z.enum(['CAMERA', 'GALLERY']);
/**
 * What the handset thought of a photograph before it was uploaded — PWA-13.
 *
 * MEASURED ON THE DEVICE, NOT ON THE SERVER, and the reason is timing rather
 * than capability. `ai-svc` decodes every image anyway and could compute all of
 * this for nothing — but drafts sit on the phone until the user presses
 * **Analisis**, so the only moment a warning can change what happens is before
 * that. A verdict that arrives with the reading arrives after the salesperson has
 * left the shelf.
 *
 * `OK` IS A RECORDED VALUE, NOT AN ABSENCE. Null means nobody looked — a photo
 * from a build predating this, or one whose check failed. Collapsing the two
 * would make "we measured it and it was fine" indistinguishable from "we have no
 * idea", which is the difference every threshold this feeds has to be set from.
 *
 * THE USER WAS WARNED AND SENT IT ANYWAY is not a separate field. It is
 * `captureQuality != 'OK'` on a photo that exists — deriving it costs a WHERE
 * clause, and storing it would be a second piece of state saying what the first
 * already says.
 */
export const CaptureQuality = z.enum([
    'OK', //       Sharp enough and bright enough.
    'BLURRED', //  Low variance of Laplacian at a normalised size.
    'DARK', //     Mean luma below the floor; reported ahead of blur, since a dark
    //             frame also scores low on sharpness for a reason the user cannot
    //             act on by holding the phone steadier.
]);
/**
 * EXIF metadata as read on the client.
 *
 * FR-04 is explicit that this is an audit trail input and NOT a blocker: a photo
 * with no EXIF still uploads, and merely raises FR-16 signal 3.
 */
export const ExifMetadata = z.object({
    timestamp: Timestamp.nullable(),
    gps: GeoPoint.nullable(),
    deviceSignature: z.string().max(200).nullable(),
    /** False when the file carried no readable EXIF at all — FR-16 signal 3. */
    present: z.boolean(),
});
/** Step 2 of the TAD 4.3 pipeline: client asks for a presigned PUT. */
export const PresignRequest = z.object({
    visitId: Id,
    contentType: z.enum(['image/jpeg', 'image/webp']),
    contentLength: z.number().int().positive().max(10 * 1024 * 1024),
});
/**
 * TSP 7.3 A2: the signature must cover the full object key, not a prefix, and
 * the URL must expire. `expiresAt` is returned so the client can re-request
 * rather than retrying a dead URL from the IndexedDB queue.
 */
export const PresignResponse = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string().min(1),
    expiresAt: Timestamp,
});
/**
 * Step 4: metadata POST after the client has uploaded direct to Spaces.
 * The image itself never transits the application tier (TAD 4.3).
 */
export const PhotoMetadataRequest = z.object({
    visitId: Id,
    objectKey: z.string().min(1),
    captureSource: CaptureSource,
    exif: ExifMetadata,
    /** Client clock at capture; compared against EXIF for FR-16 signal 2. */
    capturedAt: Timestamp,
    compressedBytes: z.number().int().positive(),
    /**
     * The pixel dimensions of what was stored, and of what the camera handed the
     * client before it compressed anything.
     *
     * ALL OPTIONAL, and that is not laziness about validation. A photo queued by a
     * build that predates these fields drains through the same endpoint after the
     * app updates, and a required field would answer that photo with a 400 it can
     * never satisfy — the retry loop would hold it until the visit closed. Absent
     * means "captured before we recorded this", which the nullable columns behind
     * this schema say exactly.
     *
     * `source*` is the interesting half: the client opens the OS picker's camera
     * rather than the OS Camera app, and some Android OEMs give an intent caller a
     * smaller image than the same camera saves to its own gallery. Grouped by
     * `exif_device_signature`, these turn "the photos look worse" into a list of
     * handsets — which is the only way to decide whether an in-app viewfinder is
     * worth its own risks.
     */
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    sourceWidth: z.number().int().positive().optional(),
    sourceHeight: z.number().int().positive().optional(),
    sourceBytes: z.number().int().positive().nullish(),
    /**
     * The on-device quality check — PWA-13.
     *
     * OPTIONAL FOR THE REASON THE DIMENSIONS ABOVE ARE, which that comment sets out
     * in full: a draft staged by a build predating these fields drains through this
     * same endpoint after the app updates, and a required field would answer it
     * with a 400 it can never satisfy.
     *
     * `captureSharpness` RIDES ALONG WITH THE VERDICT rather than being derived
     * from it, and it is the more valuable of the two. `captureQuality` is this
     * build's opinion against a threshold nobody has measured yet; the raw number
     * is what lets that threshold be re-chosen from a few thousand real captures
     * and every stored photo re-judged against it, without re-collecting anything.
     * Same argument as `PriceObservation.rawText`.
     */
    captureQuality: CaptureQuality.optional(),
    captureSharpness: z.number().nonnegative().optional(),
    idempotencyKey: IdempotencyKey,
});
export const PhotoRecord = z.object({
    id: Id,
    visitId: Id,
    objectKey: z.string(),
    /** Null until the analysis resolves it — see `PhotoType`. */
    photoType: PhotoType.nullable(),
    captureSource: CaptureSource,
    capturedAt: Timestamp,
    uploadedAt: Timestamp,
    /** Processing status of the AI pipeline for this photo. */
    processing: z.enum(['QUEUED', 'RUNNING', 'DONE', 'FAILED']),
    /**
     * When the image bytes were deleted under the NFR-07 retention purge
     * (ADR-0011). Null means the image is still fetchable.
     *
     * `objectKey` above stays populated either way, so a client CANNOT infer
     * availability from it — this is the field to branch on. Render a placeholder
     * rather than an <img>: the key still signs successfully and the request then
     * 404s, which looks like a bug rather than a policy.
     */
    purgedAt: Timestamp.nullable(),
});
/**
 * Client-side upload queue item (TAD 3.2.2).
 *
 * This is transmission durability ONLY — it holds already-captured images until
 * the network returns. It does not permit new work to begin offline, which
 * remains out of scope per SPK Pasal 2(4).
 */
export const QueuedUpload = z.object({
    localId: z.string(),
    visitId: Id,
    captureSource: CaptureSource,
    exif: ExifMetadata,
    capturedAt: Timestamp,
    /**
     * True while this is a draft the user has taken but not yet sent.
     *
     * FR-04's capture flow stages up to seven photos locally so they can be
     * reviewed, retaken or discarded before any of them is uploaded. A staged row
     * is skipped by the drain loop, which is what stops the background
     * auto-drain from sending a photo the user is still deciding about.
     */
    staged: z.boolean(),
    /** 1-based grid position. Survives a retake so the tile does not jump. */
    slot: z.number().int().positive(),
    /**
     * The capture check's verdict, carried from the shutter to the drain — PWA-13.
     *
     * ON THE QUEUE RECORD BECAUSE THE TWO MOMENTS ARE HOURS APART. The measurement
     * happens when the photo is taken; the registration that persists it happens
     * when the user presses **Analisis**, possibly after the app has been killed and
     * relaunched. Re-measuring at drain time would work and is worse: it would put
     * a JPEG decode inside the upload loop, and it would score the compressed copy
     * rather than the one the user was shown a warning about.
     */
    captureQuality: CaptureQuality.optional(),
    captureSharpness: z.number().nonnegative().optional(),
    attempts: z.number().int().nonnegative(),
    lastError: z.string().nullable(),
});
/**
 * FR-05: the user disagrees with what the model decided the photo shows.
 *
 * Deliberately NOT a seventh `CorrectionType`. A correction is a statement about
 * one detection inside a reading; this discards the reading and asks for another
 * one, and modelling it as a correction would need a `correction_type` enum
 * migration to express something the audit trail already records.
 */
export const PhotoTypeOverrideRequest = z.object({
    photoType: PhotoType,
});
/**
 * What `POST /photos/:id/reanalyse` answers with.
 *
 * NO BODY ON THE REQUEST, which is the point of the endpoint existing at all.
 * The override above re-queues a photo as a side effect of re-classifying it,
 * so recovering from a failed analysis meant asserting a shelf-vs-menu decision
 * FR-05 took away from the user — and it refuses once corrections exist. This
 * asks only for the same bytes to be read again.
 *
 * `processing` comes back so the caller can render the new state without waiting
 * for the next poll: the card returns to its spinner on the same tap that
 * requested the re-read, rather than sitting on the error for up to 1.5 seconds
 * looking as though nothing happened.
 */
export const ReanalyseResponse = z.object({
    photoId: Id,
    processing: PhotoRecord.shape.processing,
});
//# sourceMappingURL=photo.js.map